import { useEffect, useRef, type ReactNode } from "react";

interface DialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}

/**
 * Wraps the native `<dialog>` so focus trapping, Escape handling and the
 * backdrop come from the platform rather than from a modal library.
 */
export function Dialog({
  open,
  title,
  onClose,
  children,
  footer,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    if (open && !element.open) element.showModal();
    if (!open && element.open) element.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClose={onClose}
      className="fixed inset-0 m-auto w-[min(28rem,calc(100vw-2rem))] rounded-xl bg-panel p-0 text-neutral-100 shadow-2xl backdrop:bg-black/60"
    >
      <div className="flex flex-col gap-4 p-6">
        <h2 className="text-lg font-semibold">{title}</h2>
        <div className="text-sm text-neutral-300">{children}</div>
        <div className="flex justify-end gap-2">{footer}</div>
      </div>
    </dialog>
  );
}
